/**
 * Туман войны: покрывало на всю карту с прорезями в разведанных сотах (§9.1, §23.2).
 *
 * Назначение файла: собрать геометрию тумана. Раньше туман рисовался как заливка
 * каждой неразведанной соты, и от этого он закрывал только те соты, которые сервер
 * успел отдать в кадре: за краем кадра и ниже порога отрисовки сетки карта была
 * открыта. Здесь наоборот: сплошное покрывало поверх всего мира, а разведанное
 * вырезается из него дырками. Что не вырезано — закрыто, и так по всей карте.
 *
 * Импорты: h3-js склеивает соседние соты в один контур (cellsToMultiPolygon),
 * поэтому у покрывала одна дырка на весь разведанный участок, а не по дырке на соту.
 */

import type { Feature, FeatureCollection, Polygon, Position } from "geojson";
import type { FogState } from "@hive/shared";
import type { MapHexListItem } from "@hive/shared/api/map";
import { cellToBoundary, cellsToMultiPolygon } from "h3-js";

/**
 * Границы покрывала. Широта обрезана по пределу проекции Меркатора, который
 * использует MapLibre: за 85° полигон растягивается в бесконечность.
 */
const WORLD_RING: Position[] = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

/** Уровни тумана, которые считаются просветом в покрывале. */
const LIFTED: readonly FogState[] = ["visible", "silhouette"];

export interface FogProperties {
  /** Плотность: сплошное покрывало или приподнятый силуэт. */
  readonly layer: "blanket" | "silhouette";
}

/** Соты, которые вырезаются из покрывала, и соты-силуэты — отдельно. */
export function splitByFog(hexes: readonly MapHexListItem[]): {
  lifted: string[];
  silhouette: string[];
} {
  const lifted: string[] = [];
  const silhouette: string[] = [];
  for (const hex of hexes) {
    if (!LIFTED.includes(hex.fog)) continue;
    lifted.push(hex.hexId);
    if (hex.fog === "silhouette") silhouette.push(hex.hexId);
  }
  return { lifted, silhouette };
}

/** Контур соты в порядке GeoJSON (долгота, широта) с замыкающей точкой. */
function cellRing(hexId: string): Position[] {
  const ring = cellToBoundary(hexId).map(([lat, lon]) => [lon, lat] as Position);
  const first = ring[0];
  if (first) ring.push(first);
  return ring;
}

/**
 * Геометрия тумана для MapLibre.
 *
 * Первая фигура — покрывало: внешнее кольцо по всему миру, дырки — разведанные
 * участки. Дырка в дырке (замкнутый неразведанный островок внутри разведанного
 * района) полигоном с дырками не выражается, поэтому такие островки добавляются
 * отдельными фигурами покрывала.
 */
export function fogGeoJson(
  liftedCells: readonly string[],
  silhouetteCells: readonly string[],
): FeatureCollection<Polygon, FogProperties> {
  const holes: Position[][] = [];
  const islands: Feature<Polygon, FogProperties>[] = [];

  if (liftedCells.length > 0) {
    // cellsToMultiPolygon(..., true) отдаёт координаты как [lon, lat] — формат GeoJSON.
    for (const polygon of cellsToMultiPolygon([...liftedCells], true)) {
      const [outer, ...inner] = polygon;
      if (outer) holes.push(outer);
      for (const ring of inner) {
        islands.push({
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [ring] },
          properties: { layer: "blanket" },
        });
      }
    }
  }

  const blanket: Feature<Polygon, FogProperties> = {
    type: "Feature",
    id: 0,
    geometry: { type: "Polygon", coordinates: [WORLD_RING, ...holes] },
    properties: { layer: "blanket" },
  };

  const silhouettes = silhouetteCells.map<Feature<Polygon, FogProperties>>((hexId) => ({
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [cellRing(hexId)] },
    properties: { layer: "silhouette" },
  }));

  return {
    type: "FeatureCollection",
    features: [blanket, ...islands, ...silhouettes],
  };
}
